import {
    ArrayUtil, CompanySettings, GeneralSettings, getThemeColor, Keys, MenuItem, Navigation, ObjectUtil, ShiftedNumbers
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ButtonVariant, ClearButtonVisible, Component, HorizontalSpacer, Image, ImageType, KeyEvent, KeyHandler,
    KeyModifiers, Label, MainPageAccessor, Overlay, Panel, PanelProps, PanelStyles, Textbox, Tree, TreeNode
} from "@mcleod/components";
import { MenuFavorites } from "./MenuFavorites";
import { MenuLauncher } from "./MenuLauncher";
import { MenuTreeNodeBranch } from "./MenuTreeNodeBranch";
import { MenuTreeNodeLeaf } from "./MenuTreeNodeLeaf";
import { PanelFavorites } from "./PanelFavorites";
import { PanelToolbox } from "./PanelToolbox";

export class MainMenu extends Panel {
    panelHeader: Panel;
    //used for the toolbox portion of the menu
    panelToolbox: PanelToolbox;
    //used for the favorites portion of the menu
    panelFavorites: PanelFavorites;
    //used for the regular menu (including the filter)
    textSearchMenu: Textbox;
    treeMenu: Tree<MenuTreeNodeLeaf>;
    //other
    overlay: Overlay;
    menuLauncher: MenuLauncher;
    constructor(props?: Partial<PanelProps>) {
        super({
            backgroundColor: "subtle.lightest",
            color: "subtle.darker",
            width: 300,
            height: 0,
            padding: 0,
            ...props });
        const settings = GeneralSettings.get();
        if (settings == null || settings.menu == null)
            this.add(new Label({ text: "The menu hasn't been loaded.", color: "subtle.light" }));
        else {
            this.menuLauncher = new MenuLauncher(this);
            this.createFilteredMenu();
            this.add(this.createHeaderPanel())
            this.panelFavorites = new PanelFavorites(this, { id: "panelMenuFavorites", fillRow: true, scrollY: true });
            this.panelToolbox = new PanelToolbox(this, settings.menu_toolbox_items);
            this.add(this.panelToolbox);
            this.add(this.panelFavorites);
            this.add(this.textSearchMenu);
            this.add(this.treeMenu);
            this.populateMenuTree();
        }
    }

    get treeFavoritesMenu(): Tree<MenuTreeNodeLeaf> {
        return this.panelFavorites.treeFavoritesMenu;
    }

   hide() {
        this.unselectAllMenus();
        Overlay.hideOverlay(this.overlay);
    }

    private createHeaderPanel(): Panel {
        const result = new Panel({ id: "headerPanel", fillRow: true});
        const menuLabel = new Label({
            text: "Menu",
            allowSelect: false,
            fontSize: "xxxlarge",
            fontBold: true,
            color: "component.palette.menu.mainColor",
            rowBreak: false
        });

        const closeButton = new Button({
            color: "component.palette.menu.secondaryColor",
            imageName: ImageName.x,
            imageHeight: 28,
            imageWidth: 28,
            variant: ButtonVariant.round,
            onClick: () => this.hide()
        });
        result.add(menuLabel, new HorizontalSpacer(), closeButton);
        return result;
    }

    getHeaderHomeComponent(): Component {
        let result: Component = null;
        const logo = CompanySettings.get()?.header_logo;
        if (logo != null) {
            result = new Image({
                id: "imageLogo",
                imageType: ImageType.IMG,
                imageBytes: logo,
                height: 30,
                width: "auto",
            });
        } else {
            result = new Button({
                imageName: ImageName.home,
                imageHeight: 24,
                imageWidth: 24,
                variant: ButtonVariant.round,
                color: "primary.darker",
                tooltip: "Return to your home page",
            });
        }
        result.addClickListener(() => {
            Navigation.navigateTo("");
            this.hide();
        });
        return result;
    }

    private createFilteredMenu() {
        this.textSearchMenu = new Textbox({
            placeholder: "Search",
            color: "background2-reverse",
            captionVisible: false,
            clearButtonVisible: ClearButtonVisible.WHEN_FOCUSED,
            fillRow: true,
            margin: 4
        });
        this.textSearchMenu._inputDiv.style.backgroundColor = getThemeColor("primary.reverse");
        this.textSearchMenu.imagePre = new Image({
            name: ImageName.magnifyingGlass,
            color: "strokePrimary",
            paddingLeft: 4,
            height: 24,
            width: 24
        });

        this.textSearchMenu.addFocusListener(() => this.unselectMenus(this.treeFavoritesMenu));
        this.textSearchMenu.addChangeListener(() => this.populateMenuTree());
        this.textSearchMenu.addKeyDownListener((event: KeyEvent) => this.handleSharedKeys(event)); //in theory we shouldn't need this, but the ALL key handler below doesn't hear left/right arrow keys?
        this.treeMenu = new Tree({
            id: "mainMenuTree",
            fillRow: true,
            fillHeight: true,
            borderWidth: 0,
            paddingLeft: 0,
            selectedNodeBackgroundColor: "component.palette.hover.backgroundColor",
            selectedNodeColor: (node) => node.color
        });

        this.treeMenu.setClassIncluded(PanelStyles.slimScrollbar);
        this.treeMenu.addChangeListener(() => this.unselectMenus(this.treeFavoritesMenu));
    }

    clearSearch() {
        if (this.textSearchMenu != null) {
            this.textSearchMenu.text = "";
        }
    }

    show(anchor: Component) {
        this.setProps({ style: { transition: "" }, height: 0, padding: 0 });
        this.clearSearch();
        Overlay.alignToAnchor(this, anchor); // really just to get the CSS since we are positioning manually
        this.setProps({ left: 0, top: MainPageAccessor.pageHeaderHeight, style: { transition: "height 250ms, opacity 500ms" } })
        this.overlay = Overlay.showInOverlay(this);
        this.focus();
        setTimeout(() => this.setProps({ height: `calc(100vh - ${MainPageAccessor.pageHeaderHeight}px)`, padding: 8, borderWidth: 0 }), 0);
    }

    override focus() {
        if (this.textSearchMenu != null)
            this.textSearchMenu.focus();
    }

    private populateMenuTree() {
        const filter = this.textSearchMenu.text.toLowerCase();
        const root = new TreeNode({ tree: this.treeMenu });
        let filtered = GeneralSettings.get().menu.items;
        if (filter?.length > 0) {
            filtered = ObjectUtil.deepCopy(filtered);
            filtered = ObjectUtil.filterRecursiveArrayContainingString<MenuItem>(filtered, "items", "caption", filter);
        }
        for (const item of filtered)
            this.addTreeNode(root, item);
        root.removeChildrenMatching(node => node.expandedImageName != null && node.getChildCount() === 0);
        if (filter.length > 0)
            root.expandAll();
        this.treeMenu.getRootNode().setChildren(root.getChildren());
        this.treeMenu.selectedNode = null;
        const soleLeaf = this.getSoleLeaf(root);
        if (soleLeaf != null)
            this.treeMenu.selectedNode = soleLeaf;
    }

    private getSoleLeaf(node) {
        if (node.getChildCount() > 1 || (node.isRoot() && node.getChildCount() === 0))
            return null;
        else if (node.getChildCount() === 0)
            return node;
        else
            return this.getSoleLeaf(node.getChild(0));
    }

    private addTreeNode(parent: TreeNode, item: MenuItem) {
        if (item.items == null) {
            const child = new MenuTreeNodeLeaf(this, {menuItem: item});
             // When hovering over this node, the background changes to match the selected parent.
             // This aligns the left edge with the parent node
            child.paddingLeft = 20;
            // allows favorite star to align with the stars in panelFavorites.
            child.paddingRight = 4;
            parent.addChild(child);
        }
        else {
            const node = new MenuTreeNodeBranch({
                menuItem: item,
                color: parent.isRoot() ? "component.palette.menu.mainColor" : "component.palette.menu.secondaryColor"
            });
            parent.addChild(node);
            for (const child of item.items)
                this.addTreeNode(node, child);
        }
    }

    selectFirstToolboxItem() {
        this.panelToolbox.focusFirstToolboxItem();
    }

    selectFirstFavorite() {
        this.panelFavorites.selectFirstFavorite();
    }

    override getKeyHandlers(): KeyHandler[] {
        let result = [];
        if (this.keyHandlers != null)
            result = [...this.keyHandlers];
        result.push({
            key: "ALL",
            listener: (event) => this.handleSharedKeys(event),
            element: null,
            scope: this._element
        });
        return result;
    }

    private handleSharedKeys(event: KeyEvent) {
        //set shouldAutomaticallyStopPropagation to 'false'...we can't assume that we are going to logically handle the key via the 'ALL' listener
        //for example, if the user is typing in the filter textbox, we don't want to consume the key (so their typing has an effect)
        //or if the user uses the key to refresh the page, that should work as well
        event.shouldAutomaticallyStopPropagation = false;
        if (KeyModifiers.hasModifiers({ ctrlKey: false, shiftKey: true, altKey: false }, event) === true &&
            ShiftedNumbers[event.key] != null &&
            this.showFavorite(event.domEvent, ShiftedNumbers[event.key]) === true) {
            event.preventDefault();
            event.shouldAutomaticallyStopPropagation = true;
        } else if (event.isKey(Keys.ESCAPE, {})) {
            this.hide();
            event.preventDefault();
            event.shouldAutomaticallyStopPropagation = true;
        } else
            this.processTreeKey(event);
    }

    private getNumToolboxItems(): number {
        return ArrayUtil.getLength(GeneralSettings.get().menu_toolbox_items);
    }

    private convertHotKeyStringToNumber(zeroThroughNineKey: string): number {
        if (zeroThroughNineKey < "0" || zeroThroughNineKey > "9")
            return -1;
        return zeroThroughNineKey !== "0" ? parseInt(zeroThroughNineKey) : 10;
    }

    showToolbox(event: KeyboardEvent): boolean {
        const keyAsNumber = this.convertHotKeyStringToNumber(event.key);
        if (keyAsNumber >= 1 && keyAsNumber <= this.getNumToolboxItems()) {
            const item = GeneralSettings.get().menu_toolbox_items[keyAsNumber - 1] as MenuItem;
            if (item.caption === "MPact")
                this.menuLauncher.openMpactInBrowser();
            else if (item.path.startsWith("https"))
                window.open(item.path, "", "popup");
            else
                this.menuLauncher.openPageInSlideUp(item);
            return true;
        }
        return false;
    }

    showFavorite(event: KeyboardEvent, keyValue: string): boolean {
        const keyAsNumber = this.convertHotKeyStringToNumber(keyValue);
        if (keyAsNumber >= 1 && keyAsNumber <= MenuFavorites.getFavoriteCount()) {
            const item = MenuFavorites.getFavorites()[keyAsNumber - 1];
            this.menuLauncher.openPage(item.data, event.ctrlKey);
            return true;
        }
        return false;
    }

    private processTreeKey(event: KeyEvent) {
        const activeMenuTree = this.getActiveMenuTree();
        if (activeMenuTree == null)
            return;
        if (event.key === Keys.ENTER) {
            if (activeMenuTree.selectedNode?.menuItem?.path != null)
                this.menuLauncher.openPage(activeMenuTree.selectedNode.menuItem, event.ctrlKey);
            event.preventDefault();
            event.shouldAutomaticallyStopPropagation = true;
        } else
            activeMenuTree.handleKey(event);
    }

    private getActiveMenuTree(): Tree<MenuTreeNodeLeaf> {
        //see which menu is active (has a selected node), and open the page for that node
        if (this.treeMenu.selectedNode != null || this.textSearchMenu.hasFocus())
            return this.treeMenu;
        else if (this.treeFavoritesMenu.selectedNode != null)
            return this.treeFavoritesMenu;
        return null;
    }

    unselectAllMenus() {
        this.unselectMenus(this.treeMenu, this.treeFavoritesMenu);
    }

    unselectMenus(...menus: Tree<MenuTreeNodeLeaf>[]) {
        for (const menu of menus) {
            if (menu != null) {
                menu._element.blur();
                menu.selectedNode = null;
            }
        }
    }

    static findMenuItemForPath(searchPath: string, searchMenuItem: MenuItem = GeneralSettings.get().menu): MenuItem {
        if (searchMenuItem.path === searchPath)
            return searchMenuItem;
        if (searchMenuItem.items != null) {
            for (const item of searchMenuItem.items) {
                const sub = this.findMenuItemForPath(searchPath, item);
                if (sub != null)
                    return sub;
            }
        }
        return undefined;
    }
}
